import Typography from '@liquidweb-dev/undercurrent/Typography';
import DOMPurify, { type Config } from 'dompurify';
import parse, {
	attributesToProps,
	domToReact,
	type DOMNode,
	type HTMLReactParserOptions,
} from 'html-react-parser';

import CodeBlock from 'components/CodeBlock';

/**
 * HTMLContent component that extends react-parser with automatic HTML content extraction
 * and CodeBlock component support for <pre> tags
 */
const HTMLContent = ({
	content,
	domPurifyConfig,
}: {
	content: string;
	domPurifyConfig?: Config;
}) => {
	if (!content) return null;

	const sanitizedContent = DOMPurify.sanitize(content, {
		FORBID_TAGS: ['style'],
		...domPurifyConfig,
	});

	const options: HTMLReactParserOptions = {
		replace: (domNode) => {
			if (domNode.type === 'tag' && domNode.name === 'pre') {
				return (
					<CodeBlock sx={{ marginY: 200 }}>
						{domToReact(domNode.children as DOMNode[], options)}
					</CodeBlock>
				);
			}

			if (domNode.type === 'tag' && domNode.name === 'h1') {
				return (
					<Typography
						component="h1"
						variant="title-md"
						sx={{ fontWeight: 500, marginY: 200 }}
						{...attributesToProps(domNode.attribs)}
					>
						{domToReact(domNode.children as DOMNode[], options)}
					</Typography>
				);
			}

			if (domNode.type === 'tag' && domNode.name === 'h2') {
				return (
					<Typography
						component="h2"
						variant="title-sm"
						sx={{ fontWeight: 500, marginY: 200 }}
						{...attributesToProps(domNode.attribs)}
					>
						{domToReact(domNode.children as DOMNode[], options)}
					</Typography>
				);
			}

			if (domNode.type === 'tag' && domNode.name === 'h3') {
				return (
					<Typography
						component="h3"
						variant="title-xs"
						sx={{ fontWeight: 500, marginY: 150 }}
						{...attributesToProps(domNode.attribs)}
					>
						{domToReact(domNode.children as DOMNode[], options)}
					</Typography>
				);
			}

			if (
				domNode.type === 'tag' &&
				(domNode.name === 'h4' ||
					domNode.name === 'h5' ||
					domNode.name === 'h6')
			) {
				return (
					<Typography
						component={domNode.name}
						variant="body-md"
						sx={{ fontWeight: 500, marginY: 150 }}
						{...attributesToProps(domNode.attribs)}
					>
						{domToReact(domNode.children as DOMNode[], options)}
					</Typography>
				);
			}

			if (domNode.type === 'tag' && domNode.name === 'p') {
				return (
					<Typography
						component="p"
						variant="body-md"
						{...attributesToProps(domNode.attribs)}
					>
						{domToReact(domNode.children as DOMNode[], options)}
					</Typography>
				);
			}

			return domNode;
		},
	};

	return <Typography>{parse(sanitizedContent, options)}</Typography>;
};

export default HTMLContent;
